import {AppConfig} from '@frontend/AppConfig'
import {useUserData} from '@frontend/hooks'
import type {
    ChatDetailsData,
    ChatMessageData,
    ChatMessagesViewedEventData,
    NewChatMessageEventData,
} from '@frontend/modules/private/chats/ChatsModuleTypes'
import {
    type Dispatch,
    type SetStateAction,
    useCallback,
} from 'react'
import {WebSocketEventsHandler} from 'swayok-react-mdb-ui-kit/components/WebSockets/WebSocketEventsHandler'

interface Props {
    chat: ChatDetailsData | null
    miniChat?: boolean
    disabled?: boolean
    setMessages: Dispatch<SetStateAction<ChatMessageData[]>>
    setIsReadingMessagesAllowed: Dispatch<SetStateAction<boolean>>
}

// Обработчики WebSocket событий для чата.
export function ChatDetailsWebsocketHandlers(props: Props) {

    const {
        chat,
        miniChat,
        disabled,
        setMessages,
        setIsReadingMessagesAllowed,
    } = props

    const user = useUserData()

    /*
     * Событие: сообщения чата просмотрены другим пользователем.
     * useCallback уменьшает количество переподписок на события, т.к. иначе при
     * каждой перерисовке будет создаваться новая функция, которая в свою очередь
     * будет пересоздавать подписку на события. Поэтому пересоздание функции будет
     * происходить только при изменении списка сообщений.
     */
    const chatMessagesViewed = useCallback((eventData: ChatMessagesViewedEventData): void => {
        if (!chat || chat.id !== eventData.chatId || disabled) {
            // Это событие для другого чата, или сейчас загружаются последние сообщения.
            return
        }
        if (eventData.messagesIds.length > 0) {
            setMessages(messages => {
                const updates = messages.slice()
                // Событие относится к этому чату.
                let hasChanges: boolean = false
                for (let i = 0; i < updates.length; i++) {
                    if (eventData.messagesIds.includes(updates[i].id)) {
                        // Помечаем сообщение прочитанным.
                        updates[i] = {...updates[i], isViewed: true}
                        hasChanges = true
                    }
                }
                if (hasChanges && !disabled) {
                    // Есть изменения: обновляем состояние списка сообщений.
                    return updates
                }
                return messages
            })
        }
    }, [disabled])

    /*
     * Событие: получено новое сообщение в чат.
     * Про использование useCallback смотри комментарии к chatMessagesViewed().
     */
    const receivedNewChatMessage = useCallback((eventData: NewChatMessageEventData): void => {
        if (!user || !chat || chat.id !== eventData.chat.id || disabled) {
            // Это событие для другого чата, или сейчас загружаются последние сообщения.
            return
        }

        setMessages(messages => {
            // Ищем существующее сообщение.
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].id === eventData.message.id) {
                    // Это сообщение уже есть в чате: игнорируем событие.
                    return messages
                }
            }
            // Сообщения еше нет в чате: добавляем.
            const newMessage: ChatMessageData = {...eventData.message}
            if (newMessage.user.id === user.id) {
                newMessage.isAuthor = true
            } else {
                /*
                 * Активируем возможность отправлять запросы на
                 * прочтение новых сообщений при взаимодействии с чатом.
                 */
                setTimeout(
                    () => setIsReadingMessagesAllowed(true),
                    1000
                )
            }
            return messages.concat([newMessage])
        })
    }, [user?.id, disabled])

    if (!user || !chat || chat.otherUserHasDeletedChat) {
        return null
    }

    return (
        <>
            <WebSocketEventsHandler<ChatMessagesViewedEventData>
                key={`chat-${chat.id}-messages-viewed${miniChat ? '-mini-chat' : ''}`}
                subscriptionId={`chat-${chat.id}-messages-viewed${miniChat ? '-mini-chat' : ''}`}
                event={AppConfig.websocketEvent.chatMessagesViewed}
                handler={chatMessagesViewed}
            />
            <WebSocketEventsHandler<NewChatMessageEventData>
                key={`chat-${chat.id}-new-message${miniChat ? '-mini-chat' : ''}`}
                subscriptionId={`chat-${chat.id}-new-message${miniChat ? '-mini-chat' : ''}`}
                event={AppConfig.websocketEvent.newChatMessage}
                handler={receivedNewChatMessage}
            />
        </>
    )
}

